import { useQuery, useMutation, useQueryClient } from 'react-query';
import { API_ENDPOINTS } from '@framework/utils/api-endpoints';
import http from '@framework/utils/http';
import { ShortCart } from '@framework/types';
import { useUI } from '@contexts/ui.context';
export interface User {
  id: number;
  fboIdnumber: string;
  firstName: string;
  level: string;
  lastName: string;
}
export interface Photo {
  x238: string;
  photoX238: string;
  x318: string;
  photoX318: string;
  x318blur: string;
  photoX318blur: string;
  x600: string;
  photoX600: string;
  x175: string;
  photoX175: string;
  x225: string;
  photoX225: string;
  x150: string;
  photoX150: string;
  x142: string;
  photoX142: string;
  x345: string;
  photoX345: string;
  x90: string;
  photoX90: string;
}
export interface Item {
  id: number;
  amount: number;
  boon: any;
  category: number;
  cc: number;
  currency: string;
  dpPrice: number;
  expirationDate: any;
  expirationMonth: any;
  expirationYear: any;
  hasPromotion: boolean;
  image: string;
  isNew: boolean;
  isPopular: boolean;
  metaKeywords: string;
  name: string;
  novusPrice: number;
  photo: Photo;
  price: number;
  productCode: string;
  strPrice: string;
  weight: number;
  limits: any[];
  quantity: number;
  proms: any;
  grossCost: number;
  strGrossCost: string;
  strCost: string;
  strCashlessCost: string;
  itemCc: number;
}
export interface Cart {
  id: number;
  user: User;
  totalQuantity: number;
}
export interface CartDetail {
  retailSubTotal: string;
  ccSum: string;
  cart: Cart;
  id: number;
  items: Item[];
  subTotal: string;
  grossCost: number;
  cashlessSubTotal: number;
  discountAmount: number;
  strGrossCost: string;
  strSubTotal: string;
  strCashlessSubTotal: string;
  strDiscountAmount: string;
  ccTotal: number;
  totalQuantity: number;
  minimums: {
    everyOrder: number;
  };
  message: string | null;
  isUserFirstOrder: boolean;
  activeCoupon: any;
  strCouponSubTotal: string | null;
  isMissYouActionEnabled: boolean;
  missYouActionVariants: any[];
  choicePromos?: {
    mainProductCode: string;
    variants: {
      productCode: string;
      name: string;
    }[];
  }[];
  nextLevelMessage: string;
}
export interface CartList {
  strSubTotal: string;
  ccTotal: number;
  totalQuantity: number;
  carts: CartDetail[];
  MAX_CONSOLIDATED_CARTS: number;
}
export const fetchCartList = async (): Promise<ShortCart> => {
  const {
    data
  } = await http.get(API_ENDPOINTS.MINI_CART);
  return data;
};
export const useCartQuery = () => {
  const {
    isAuthorized
  } = useUI();
  return useQuery<ShortCart>('cart', fetchCartList, {
    enabled: isAuthorized,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};
const fetchCartListAll = async (): Promise<CartList> => {
  const {
    data
  } = await http.get(API_ENDPOINTS.CART_LIST);
  return data;
};
export const useCartListQuery = () => {
  const {
    isAuthorized
  } = useUI();
  return useQuery<CartList>(['cartList'], fetchCartListAll, {
    enabled: isAuthorized,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};
export const addToCart = async ({
  productCode,
  quantity,
  isAuthorized,
  cartId
}: {
  productCode: string;
  quantity: number;
  isAuthorized?: boolean;
  cartId?: number | null;
}) => {
  let effectiveCartId: number | null = cartId ?? null;
  if (!effectiveCartId && typeof window !== 'undefined') {
    const activeSub = localStorage.getItem('activeSubCartId');
    if (activeSub) {
      effectiveCartId = Number(activeSub);
    }
  }
  if (!effectiveCartId && !isAuthorized && typeof window !== 'undefined') {
    const storedCartId = localStorage.getItem('guestCartId');
    if (storedCartId) {
      effectiveCartId = Number(storedCartId);
    }
  }
  const payload = {
    cartId: effectiveCartId,
    products: [{
      product: productCode,
      quantity
    }]
  };
  const {
    data
  } = await http.post(API_ENDPOINTS.ADD_TO_CART, payload);
  return data;
};
export const useAddToCartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(addToCart, {
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
      queryClient.invalidateQueries('cartList');
      queryClient.invalidateQueries('guestCartList');
    }
  });
};
export const updateCartItemQuantity = async ({
  cartId,
  productCode,
  quantity,
  isAuthorized
}: {
  cartId: number | null;
  productCode: string;
  quantity: number;
  isAuthorized?: boolean;
}) => {
  let effectiveCartId: number | null = cartId ?? null;
  if (!effectiveCartId && typeof window !== 'undefined') {
    const activeSub = localStorage.getItem('activeSubCartId');
    if (activeSub) {
      effectiveCartId = Number(activeSub);
    }
  }
  if (!effectiveCartId && !isAuthorized && typeof window !== 'undefined') {
    const storedCartId = localStorage.getItem('guestCartId');
    if (storedCartId) {
      effectiveCartId = Number(storedCartId);
    }
  }
  const payload = {
    cartId: effectiveCartId,
    items: [{
      productCode,
      quantity
    }]
  };
  const {
    data
  } = await http.post(API_ENDPOINTS.UPDATE_CART, payload);
  return data;
};
export const useUpdateCartQuantityMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(updateCartItemQuantity, {
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
      queryClient.invalidateQueries('cartList');
      queryClient.invalidateQueries('guestCartList');
    }
  });
};
const cleanCart = async () => {
  const {
    data
  } = await http.post(API_ENDPOINTS.CLEAN_CART);
  return data;
};
export const useCleanCartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(cleanCart, {
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
      queryClient.invalidateQueries('cartList');
      queryClient.invalidateQueries('guestCartList');
    }
  });
};
export const removeFromCart = async ({
  productCode,
  cartId,
  isAuthorized
}: {
  productCode: string;
  cartId?: number | null;
  isAuthorized?: boolean;
}) => {
  let effectiveCartId: number | null = cartId ?? null;
  if (!effectiveCartId && typeof window !== 'undefined') {
    const activeSub = localStorage.getItem('activeSubCartId');
    if (activeSub) {
      effectiveCartId = Number(activeSub);
    }
  }
  if (!effectiveCartId && !isAuthorized && typeof window !== 'undefined') {
    const storedCartId = localStorage.getItem('guestCartId');
    if (storedCartId) {
      effectiveCartId = Number(storedCartId);
    }
  }
  const {
    data
  } = await http.post(API_ENDPOINTS.REMOVE_FROM_CART, {
    productCode,
    cartId: effectiveCartId
  });
  return data;
};
export const useRemoveFromCartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(removeFromCart, {
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
      queryClient.invalidateQueries('cartList');
      queryClient.invalidateQueries('guestCartList');
    }
  });
};
export const deleteCart = async ({
  cartId,
  isAuthorized
}: {
  cartId: number | null;
  isAuthorized?: boolean;
}) => {
  let effectiveCartId: number | null = cartId ?? null;
  if (!effectiveCartId && typeof window !== 'undefined') {
    const activeSub = localStorage.getItem('activeSubCartId');
    if (activeSub) {
      effectiveCartId = Number(activeSub);
    }
  }
  if (!effectiveCartId && !isAuthorized && typeof window !== 'undefined') {
    const storedCartId = localStorage.getItem('guestCartId');
    if (storedCartId) {
      effectiveCartId = Number(storedCartId);
    }
  }
  const {
    data
  } = await http.post(API_ENDPOINTS.DELETE_CART, {
    cartId: effectiveCartId
  });
  return data;
};
export const useDeleteCartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteCart, {
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
      queryClient.invalidateQueries('cartList');
      queryClient.invalidateQueries('guestCartList');
    }
  });
};
export const fetchGuestCartList = async (): Promise<CartList> => {
  if (typeof window === "undefined") {
    throw new Error("Local Storage is not accessible");
  }
  const guestCartId = localStorage.getItem("guestCartId");
  if (!guestCartId) {
    throw new Error("Guest Cart ID not found");
  }
  const {
    data
  } = await http.get(`${API_ENDPOINTS.GUEST_CART_LIST}${guestCartId}`);
  return data;
};
export const useGuestCartListQuery = ({
  enabled = true
}: {
  enabled?: boolean;
} = {}) => {
  return useQuery<CartList>(['guestCartList'], fetchGuestCartList, {
    enabled: enabled && typeof window !== "undefined" && !!localStorage.getItem("guestCartId"),
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};
export const createConsolidatedCart = async ({
  consolidateWith,
  fboId
}: {
  consolidateWith: number;
  fboId: string;
}) => {
  const payload = {
    consolidateWith,
    fboId
  };
  const {
    data
  } = await http.post(API_ENDPOINTS.CART_CREATE, payload);
  return data;
};
export const useCreateConsolidatedCartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(createConsolidatedCart, {
    onSuccess: () => {
      queryClient.invalidateQueries('cartList');
      queryClient.invalidateQueries('cart');
    }
  });
};